/**
 * Adapts an engine `QueryResponse` into the `PnlPivotModel` that `PnlReportGrid` renders: an ordered
 * row list with indent/emphasis/scaling plus a positional cell matrix.
 *
 * Two spellings must match or the grid silently renders blanks: cell fields are `pnlAxisField(index)`,
 * and row metadata keys are `axis_kind` / `axis_indent` / `axis_emphasis` / `scaling`.
 */

import type { PnlAxisColumn } from 'src/components/star-grid/pnlAxis.types';
import type { QueryResponse, ReportQueryColumn, ReportQueryRow } from 'src/components/star-grid/api/reportEngineTypes';
import { REPORT_ROW_ID } from 'src/components/star-grid/reportDefinitionConstants';
import { PNL_SECONDARY_LINE_DIMENSION, PNL_STATEMENT_LINE_DIMENSION } from 'src/components/star-grid/starGridConstants';
import type { PnlGridRow, PnlPivotModel } from 'src/components/star-grid/starGridTypes';
import { pnlAxisColumnGroups, pnlAxisField } from 'src/components/star-grid/utils/pnlAxisColumns';
import { scenarioPeriodType } from 'src/components/star-grid/utils/scenarioPeriodType';
import { reportRowKey } from 'src/components/star-grid/hooks/useReportDrill';

/** The calc a percentage column carries — its cells are fractions, so the display scale must not apply. */
const VARIANCE_PCT_CALC = 'variance_pct';

const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Readable leaf header for a period. Periods arrive as the cube's own keys (`202401`, `2024Q1`),
 * which are unreadable stacked twelve across under one scenario.
 */
export function periodLabel(period: string): string {
  const month = /^(\d{4})(0[1-9]|1[0-2])$/.exec(period);
  if (month) return `${MONTH_ABBREVIATIONS[Number(month[2]) - 1]}-${month[1]}`;
  const quarter = /^(\d{4})\s*Q([1-4])$/.exec(period);
  if (quarter) return `Q${quarter[2]}-${quarter[1]}`;
  return period;
}

/** True for a rollup / total line. The engine serialises a boolean; the contract names `'bold'`. */
export function isBoldRow(row: ReportQueryRow): boolean {
  return row.emphasis === 'bold' || row.emphasis === true;
}

/**
 * The tokens a deliberately blank separator column arrives as.
 *
 * A Prophecy column axis authors its gaps as real tuples (`['EMPTY','EMPTY','000000']`), so the gap
 * comes back as a column with nothing to head it and no cell to read. Both the sentinel spellings and
 * the empty strings are accepted because the engine is being taught to send the latter.
 */
const SPACER_TOKENS = new Set(['', 'EMPTY', '000000']);

/**
 * How the engine names a spacer in `calc`.
 *
 * The engine settled on an explicit `calc: 'spacer'` marker so a blank column skips cell resolution
 * instead of being a value column with no cells. That has to be checked BEFORE `calc` is read as
 * "this is derived", or the marker meant to identify the gap is what hides it.
 */
export const SPACER_CALC = 'spacer';

/**
 * True for that separator column.
 *
 * Treated as `spacer` rather than an ordinary period column because `pnlAxisColumnGroups` and
 * `buildReportAxisColumns` already lay a spacer out as a genuine empty gap. Left as a period column
 * it occupied its slot with a `0` under a blank header, which is the missing gap users reported.
 */
function isSpacerColumn(column: ReportQueryColumn): boolean {
  if (column.calc === SPACER_CALC) return true;
  if (column.calc) return false;
  return [column.label, column.scenario, column.period].every((value) => SPACER_TOKENS.has((value ?? '').trim()));
}

/**
 * One engine column as an axis column. The scenario becomes the group header and the period the leaf, so
 * the grid's existing two-row deck header (and its compact single-row variant) applies unchanged.
 */
function axisColumn(column: ReportQueryColumn, index: number): PnlAxisColumn {
  if (isSpacerColumn(column)) return { index, kind: 'spacer', label: '', group: null, period: null, header_path: [] };
  const scenario = column.scenario?.trim() ?? '';
  const period = column.period?.trim() ?? '';
  const derived = Boolean(column.calc);
  return {
    index,
    kind: derived ? 'variance' : 'period',
    // A derived column has no period of its own, so its own label is the only thing to head it with.
    label: period ? periodLabel(period) : column.label,
    group: scenario || null,
    period: period || null,
    header_path: scenario ? [{ id: scenario, label: scenario }] : [],
    // The deck's Act/Est line under each period. Set only on a base column: a derived one is headed
    // B/(W) (or its ex FX form), which `buildReportAxisColumns` already derives from `kind`/`exfx`.
    ...(derived || !scenario ? {} : { periodType: scenarioPeriodType(scenario) }),
    ...(column.calc === VARIANCE_PCT_CALC ? { format: { kind: 'percentage' as const } } : {}),
    ...(column.fx_scenario ? { exfx: true, fx_vintage: column.fx_scenario } : {})
  };
}

/**
 * One engine row as a grid row. `values` is positional against `response.columns`, so each cell is
 * addressed by INDEX rather than by a per-column property the row does not have.
 */
function toGridRow(row: ReportQueryRow, index: number, columnCount: number): PnlGridRow {
  const bold = isBoldRow(row);
  const cells: PnlGridRow = {};
  for (let position = 0; position < columnCount; position += 1) {
    cells[pnlAxisField(position)] = row.values?.[position] ?? null;
  }
  return {
    // The row KEY, unique across the grid: a drilled child's member recurs under every parent it
    // was drilled from, and AG Grid keeps one row per id -- keyed on the member alone, the second
    // parent's breakdown would silently overwrite the first's.
    [REPORT_ROW_ID]: reportRowKey(row),
    [PNL_STATEMENT_LINE_DIMENSION]: row.member_id,
    // A drilled child that is itself a rollup can be opened one level further, as in the deck.
    axis_expandable: row.expandable === true,
    ...(row.outer_member !== undefined ? { axis_outer_member: row.outer_member, axis_outer_expandable: row.outer_expandable === true } : {}),
    label: row.label,
    ...(row.secondary_label !== undefined ? { [PNL_SECONDARY_LINE_DIMENSION]: row.secondary_label } : {}),
    ...(typeof row.secondary_indent === 'number' ? { axis_secondary_indent: row.secondary_indent } : {}),
    row_index: index,
    // Structural rows: `valueFormatter`, `pnlAxisRowClass` and `pnlAxisRowHeight` all style headers
    // and spacers off this one key.
    axis_kind: row.heading ? 'header' : row.spacer ? 'spacer' : 'value',
    axis_indent: row.indent ?? 0,
    axis_emphasis: bold,
    // `scaling` is the deck's DIVISOR for this line. The engine returns unscaled money and reports it
    // here so the grid's own `scaleId` formatting divides once, in one place.
    scaling: row.scaling ?? null,
    // Carried so `getPnlRowScale` can tell a MONEY row at natural scale from a RATIO row: both report
    // `scaling: 1.0`, and only the money row should follow the user's chosen scale. Dropped here, the
    // scale guard (which tests `percent === false`) never fired and those lines rendered as raw
    // unscaled dollars — the truncating `479,284,…` cells. Defaulted to `false` rather than left
    // undefined for the same reason: undefined means "the row never said", which keeps divisor 1.
    percent: row.percent ?? false,
    // How a ratio row's variance is stated: `bps` for a margin (the deck's basis-point delta).
    variance_basis: row.variance_basis ?? null,
    variance_sign: row.variance_sign ?? null,
    ...cells
  };
}

/**
 * The pivot model for an engine response.
 *
 * `isRollup` because an engine row already carries its own `SUM(leaf x weight)` from the closure — letting
 * the grid aggregate children on top of that would double count. `isFlatAxis` because the rows are an
 * ordered statement whose depth is the row's own `indent`, not an AG Grid tree.
 */
export function buildReportPivotModel(response: QueryResponse | undefined): PnlPivotModel {
  const columns = response?.columns ?? [];
  const rows = response?.rows ?? [];
  const axisColumns = columns.map(axisColumn);
  // A stacked report lists both dimensions' blocks in ONE column; only a side-by-side pair has a second.
  const [outer, inner] = response?.row_layout === 'stack' ? [] : response?.row_dimensions ?? [];
  return {
    isFlatAxis: true,
    isResolvedAxis: true,
    isRollup: true,
    dimensionFields: inner ? [PNL_STATEMENT_LINE_DIMENSION, PNL_SECONDARY_LINE_DIMENSION] : [PNL_STATEMENT_LINE_DIMENSION],
    ...(inner ? { dimensionHeaders: { [PNL_STATEMENT_LINE_DIMENSION]: outer, [PNL_SECONDARY_LINE_DIMENSION]: inner } } : {}),
    axisColumns,
    // Kept in step with `axisColumns` so the columns tool panel and any exporter see one grouping.
    columnGroups: pnlAxisColumnGroups(axisColumns, []),
    calculatedColumns: [],
    rows: inner ? withOuterGroups(rows, columns.length) : rows.map((row, index) => toGridRow(row, index, columns.length))
  };
}

/**
 * Two-dimension rows laid out as Prophecy's crossjoin is: the outer label shows on the first row of
 * each run of the same outer line and is left blank beneath it, so the second column reads as that
 * line's breakdown instead of repeating "DaS - All CostCenters by Type" on every row.
 */
function withOuterGroups(rows: readonly ReportQueryRow[], columnCount: number): PnlGridRow[] {
  let previous: string | undefined;
  return rows.map((row, index) => {
    const grid = toGridRow(row, index, columnCount);
    const paired = row.secondary_label !== undefined;
    const repeats = paired && previous !== undefined && row.label === previous;
    previous = paired ? row.label : undefined;
    return repeats ? { ...grid, axis_outer_repeat: true } : grid;
  });
}
